import { DeleteFilled } from "@ant-design/icons";
import { faClockRotateLeft, faEye, faLock, faPenToSquare, faProjectDiagram } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Tooltip } from "antd";
import SlideoutMenu from "components/atoms/SlideoutMenu";
import CypressTestIds from "../../../cypress/CypressTestIds";

interface IActionButtonsProps {
    schema: ISchema;
    selected?: boolean;
    permission: IPermissionState;
    runningJobsCount?: number;
    onViewSchemaModel: (id: string) => void;
    onEditSchemaModel: (id: string) => void;
    onViewSchemaModelHistory: (id: string) => void;
    onViewSchemaModelLineage: (id: string) => void;
    onDeleteSchemaModel: (s: ISchema) => void;
}

export default function SchemaModelActionButtons({
    schema,
    selected = false,
    permission,
    runningJobsCount,
    onViewSchemaModel,
    onEditSchemaModel,
    onViewSchemaModelHistory,
    onViewSchemaModelLineage,
    onDeleteSchemaModel,
}: IActionButtonsProps) {
    const { schemaId } = schema;

    return (
        <SlideoutMenu>
            {!!permission.canEdit && (
                <Tooltip title="Edit Schema">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faPenToSquare} />}
                        disabled={!permission.canEdit}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onEditSchemaModel(schemaId);
                        }}
                        data-testid={CypressTestIds.SCHEMA_LIST_EDIT_BUTTON}
                    />
                </Tooltip>
            )}
            {!!permission.canView && (
                <Tooltip title="View Schema">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faEye} />}
                        disabled={!permission.canView}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onViewSchemaModel(schemaId);
                        }}
                        data-testid={CypressTestIds.SCHEMA_LIST_VIEW_BUTTON}
                    />
                </Tooltip>
            )}
            {!!permission.canView && (
                <Tooltip title="Schema Change History">
                    <Button
                        type="link"
                        icon={<FontAwesomeIcon icon={faClockRotateLeft} />}
                        disabled={!permission.canView}
                        onClick={(event) => {
                            event && event.stopPropagation && event.stopPropagation();
                            onViewSchemaModelHistory(schemaId);
                        }}
                        data-testid={CypressTestIds.SCHEMA_LIST_CHANGE_HISTORY_BUTTON}
                    />
                </Tooltip>
            )}
            <Tooltip placement="topRight" title="Visualise relationships">
                <Button
                    type="link"
                    icon={<FontAwesomeIcon icon={faProjectDiagram} />}
                    onClick={(event) => {
                        event && event.stopPropagation && event.stopPropagation();
                        onViewSchemaModelLineage(schemaId);
                    }}
                    data-testid={CypressTestIds.SCHEMA_LIST_VISUALISE_RELATIONSHIPS_BUTTON}
                />
            </Tooltip>
            {!!permission.canDelete || (runningJobsCount && runningJobsCount > 0) ? <div className="block h-6 w-0 border-r border-dashed border-gray-300" /> : <></>}
            {!!runningJobsCount && runningJobsCount > 0 ? (
                <Tooltip placement="topLeft" title="A job is running. Schema is locked for delete.">
                    <Button type="text" danger icon={<FontAwesomeIcon icon={faLock} />} data-testid={CypressTestIds.SCHEMA_LIST_DELETE_BUTTON} />
                </Tooltip>
            ) : (
                !!permission.canDelete && (
                    <Tooltip title="Delete Schema" placement="topLeft">
                        <Button
                            type="link"
                            danger
                            icon={<DeleteFilled />}
                            disabled={!permission.canDelete}
                            onClick={(event) => {
                                event && event.stopPropagation && event.stopPropagation();
                                onDeleteSchemaModel(schema);
                            }}
                            data-testid={CypressTestIds.SCHEMA_LIST_DELETE_BUTTON}
                        />
                    </Tooltip>
                )
            )}
        </SlideoutMenu>
    );
}
